import React, { useEffect, useRef, useState } from 'react';

const POSITION_CLASS = ['tl', 'tr', 'bl', 'br'];

/**
 * 스크린샷 스타일(중앙 원형 + 4카드) 사업부 소개 컴포넌트
 */
const BusinessDepartmentShowcase = ({
  headline,
  pill,
  pillDescription,
  featureTitle,
  featureEmphasis,
  featureDescription,
  centerLabel,
  cards = [],
  layout = 'center', // 'center' | 'rowLeft' | 'cardsRow'
}) => {
  const normalizedCards =
    layout === 'rowLeft' ? cards.slice(0, 3) : cards.slice(0, 4);
  const introRef = useRef(null);
  const featureRef = useRef(null);
  const [introVisible, setIntroVisible] = useState(false);
  const [featureVisible, setFeatureVisible] = useState(false);
  const [featureEnteredByScroll, setFeatureEnteredByScroll] = useState(false);

  useEffect(() => {
    const el = introRef.current;
    if (!el) return;

    if (typeof window !== 'undefined' && !('IntersectionObserver' in window)) {
      setIntroVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // 상단 첫 진입 시에도 눈에 띄게 약간 딜레이
          window.setTimeout(() => setIntroVisible(true), 90);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = featureRef.current;
    if (!el) return;

    if (typeof window !== 'undefined' && !('IntersectionObserver' in window)) {
      setFeatureVisible(true);
      setFeatureEnteredByScroll(true);
      return;
    }

    let wasOutOfView = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setFeatureVisible(true);
          if (wasOutOfView) {
            setFeatureEnteredByScroll(true);
            io.disconnect();
          }
        } else {
          wasOutOfView = true;
        }
      },
      { threshold: 0.2 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="deptv2">
      <section
        ref={introRef}
        className={`deptv2-intro ${introVisible ? 'is-visible' : ''}`}
      >
        <h2 className="deptv2-headline">{headline}</h2>

        {(pill || pillDescription) && (
          <div className="deptv2-pill-row">
            {pill && <span className="deptv2-pill">{pill}</span>}
            {pillDescription && (
              <p className="deptv2-pill-desc">{pillDescription}</p>
            )}
          </div>
        )}
      </section>

      <section className="deptv2-diagram-wrap">
        <div className="deptv2-diagram-inner">
          <section
            ref={featureRef}
            className={`deptv2-feature ${featureVisible ? 'is-visible' : ''}`}
          >
            <h3 className="deptv2-feature-title">{featureTitle}</h3>
            {featureEmphasis && (
              <p className="deptv2-feature-emph">{featureEmphasis}</p>
            )}
            {featureDescription && (
              <p className="deptv2-feature-desc">{featureDescription}</p>
            )}
          </section>

          {layout === 'cardsRow' ? (
            <div className={`deptv2-cards-row ${featureEnteredByScroll ? 'is-in-view' : ''} ${featureVisible && !featureEnteredByScroll ? 'is-visible-immediate' : ''}`}>
              {normalizedCards.map((c, idx) => (
                <article
                  key={`${c.title}-${idx}`}
                  className="deptv2-card deptv2-card--compact"
                >
                  <div className="deptv2-card-title">{c.title}</div>
                  <div
                    className={`deptv2-card-desc deptv2-card-desc--${
                      c.descVariant || 'tight'
                    }`}
                  >
                    {c.description}
                  </div>
                </article>
              ))}
            </div>
          ) : layout === 'rowLeft' ? (
            <div className="deptv2-diagram deptv2-diagram--row">
              <div className="deptv2-center deptv2-center--row">{centerLabel}</div>
              <div className="deptv2-row-cards">
                {normalizedCards.map((c, idx) => (
                  <article key={`${c.title}-${idx}`} className="deptv2-card deptv2-card--row">
                    <div className="deptv2-card-title">{c.title}</div>
                    <div
                      className={`deptv2-card-desc deptv2-card-desc--${
                        c.descVariant || 'tight'
                      }`}
                    >
                      {c.description}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className="deptv2-diagram">
              <div className="deptv2-center">{centerLabel}</div>

              {normalizedCards.map((c, idx) => (
                <article
                  key={`${c.title}-${idx}`}
                  className={`deptv2-card ${POSITION_CLASS[idx]}`}
                >
                  <div className="deptv2-card-title">{c.title}</div>
                  <div
                    className={`deptv2-card-desc deptv2-card-desc--${
                      c.descVariant || 'tight'
                    }`}
                  >
                    {c.description}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BusinessDepartmentShowcase;

